import { useEffect, useRef, useState } from "react";
import { getSocket } from "@/lib/socket/client";

type Handlers = Record<string, (payload: never) => void>;

/**
 * Listen to server events for as long as the caller is mounted, optionally
 * joining rooms first.
 *
 * The joins are re-sent on every `connect`, not just the first: a dropped
 * connection loses its room membership server-side, and a reconnect that
 * didn't re-join would look connected while receiving nothing. `onResync` runs
 * on each reconnect so the caller can recover whatever it missed while down —
 * the reason none of this needs a timer.
 *
 * Handlers are read through a ref because they are rebuilt every render;
 * listing them as dependencies would tear the subscription down and rebuild it
 * just as often. The room list is compared by value for the same reason.
 */
/**
 * How many mounted subscriptions are holding each room open.
 *
 * Rooms are joined per subscriber but membership belongs to the socket, which
 * is shared by the whole tab. Two components watching the same job is ordinary
 * (a page reads the scan while its progress card follows the same run), and
 * without this the first of them to unmount takes the other's events with it.
 */
const held = new Map<string, number>();

const roomTag = (event: string, id: string) => `${event}|${id}`;

function retain(event: string, id: string) {
  held.set(roomTag(event, id), (held.get(roomTag(event, id)) ?? 0) + 1);
}

/** True when that was the last holder, so the caller should leave the room. */
function release(event: string, id: string): boolean {
  const tag = roomTag(event, id);
  const remaining = (held.get(tag) ?? 1) - 1;
  if (remaining > 0) {
    held.set(tag, remaining);
    return false;
  }
  held.delete(tag);
  return true;
}

function useSocketSubscription(
  join: { event?: string; leaveEvent?: string; ids: string[] },
  handlers: Handlers,
  onResync?: () => void,
  enabled = true,
) {
  const { event, leaveEvent } = join;
  const roomIds = join.ids.filter(Boolean);
  const roomKey = roomIds.join(",");
  const latest = useRef({ handlers, onResync, roomIds });
  // Declared before the subscription below so the values it reads on mount are
  // this render's, not the ones captured when the ref was created.
  useEffect(() => {
    latest.current = { handlers, onResync, roomIds };
  });

  useEffect(() => {
    if (!enabled) return;
    const ids = latest.current.roomIds;
    if (event && ids.length === 0) return;
    const socket = getSocket();

    const joinRooms = () => {
      if (event) for (const id of ids) socket.emit(event, id);
    };
    const resync = () => {
      joinRooms();
      latest.current.onResync?.();
    };

    const listeners = Object.keys(latest.current.handlers).map((name) => {
      const listener = (...args: unknown[]) =>
        latest.current.handlers[name]?.(args[0] as never);
      socket.on(name, listener);
      return { name, listener };
    });

    if (event) for (const id of ids) retain(event, id);
    if (socket.connected) joinRooms();
    socket.on("connect", resync);

    return () => {
      socket.off("connect", resync);
      for (const { name, listener } of listeners) socket.off(name, listener);
      if (!event) return;
      for (const id of ids)
        if (release(event, id) && leaveEvent) socket.emit(leaveEvent, id);
    };
  }, [event, leaveEvent, roomKey, enabled]);
}

/** Follow one or more rooms the caller has to ask to join. */
export function useRoomEvents(
  join: { event: string; leaveEvent?: string; ids: string[] },
  handlers: Handlers,
  onResync?: () => void,
) {
  useSocketSubscription(join, handlers, onResync);
}

/** Whether the shared socket is connected right now; pollers use it as their fallback gate. */
export function useSocketConnected(): boolean {
  const [connected, setConnected] = useState(false);
  useEffect(() => {
    const socket = getSocket();
    const sync = () => setConnected(socket.connected);
    sync();
    socket.on("connect", sync);
    socket.on("disconnect", sync);
    return () => {
      socket.off("connect", sync);
      socket.off("disconnect", sync);
    };
  }, []);
  return connected;
}

/**
 * Follow the events addressed to the signed-in user.
 *
 * The server puts every authenticated socket in its own user room at connect
 * time, so there is nothing to join — which is what makes this usable for
 * changes that aren't about a workflow the page already knows the id of.
 */
export function useUserEvents(
  handlers: Handlers,
  onResync?: () => void,
  enabled = true,
) {
  useSocketSubscription({ ids: [] }, handlers, onResync, enabled);
}
